<template>
  <q-dialog
    v-model="shown"
    data-test-id="held-seat-dialog"
  >
    <q-card
      v-if="showing"
      data-test-id="held-seat-dialog-card"
    >
      <q-card-section
        class="row items-center showing-card-section"
      >
        <div class="text-h6 col ellipsis">
          {{ showing.movie.name }} - {{ showing.time.format('h:mm a') }} - {{ showing.screen.name }}
        </div>
        <q-space />
        <q-btn
          v-close-popup
          icon="close"
          dense
          flat
          round
        />
      </q-card-section>
      <!-- Marathon seating options -->
      <q-card-section
        v-if="showing.isMarathon"
        class="row items-center q-pa-none q-pb-sm"
      >
        <q-tabs
          v-model="tab"
          style="overflow: auto;"
        >
          <q-tab
            :label="showing.movie.name"
            :name="showing.id"
            class="ellipsis"
            style="width: 150px; min-width: 150px; max-width: 150px;"
          />
          <q-tab
            v-for="marathonShowing in showing.showings.data"
            :key="marathonShowing.id"
            :label="marathonShowing.movie.name"
            :name="marathonShowing.id"
            class="ellipsis"
            style="width: 150px; min-width: 150px; max-width: 150px;"
          />
        </q-tabs>
      </q-card-section>
      <!-- Showing details -->
      <q-card-section
        class="row justify-center q-pa-none"
      >
        <showing-card
          class="col q-mb-sm cursor-pointer"
          style="zoom: 1.25; max-width: 365px;"
          :showing="showing"
          :show-times="true"
          :show-progress-bar="false"
        />
      </q-card-section>
      <q-card-section
        class="items-center q-pb-none"
        style="min-width: 500px; max-width: 500px;"
      >
        <!-- Reservation progress bar (seats remaining) -->
        <div style="position: relative;">
          <div
            class="q-mx-lg q-mt-md seat-progress-bar bg-grey-5"
          >
            <div
              class="bg-primary"
              :style="{ width: (showing.ticketsSold / showing.screen.seatCount * 100) + '%' }"
            />
            <div
              :class="'bg-' + availableColor"
              :style="{ width: (seatsAvailable / showing.screen.seatCount * 100) + '%' }"
            />
            <div
              v-if="showing.reservedSeating && socialDistanceCount"
              class="bg-grey-4"
              :style="{ width: (socialDistanceCount / showing.screen.seatCount * 100) + '%' }"
            />
            <div
              v-if="showing.hasSeatChart"
              class="bg-grey-5"
              :style="{ width: (unavailableCount / showing.screen.seatCount * 100) + '%' }"
            />
            <div
              v-else
              class="bg-grey-5"
              :style="{ width: (heldSeats / showing.screen.seatCount * 100) + '%' }"
            />
          </div>
          <div
            v-if="currentSite.showAttendancePredictionInPos"
            style="position: absolute; bottom: -14px; padding-left: 2px;"
            :style="{ left: (showing.predictedAttendance / showing.screen.seatCount * 100) + '%' }"
            class="text-grey-8"
          >
            <q-icon
              class="col"
              name="arrow_drop_up"
              size="sm"
            />
          </div>
          <div
            v-if="currentSite.showAttendancePredictionInPos"
            style="position: absolute; bottom: -21px"
            :style="{ left: (showing.predictedAttendance / showing.screen.seatCount * 100) + '%' }"
            color="grey-8"
          >
            <q-badge
              style="display: block; border-style: dashed; width: 28px;"
              color="grey-8"
              class="col text-center"
              outline
            >
              {{ showing.predictedAttendance || '0' }}
              <q-tooltip
                class="text-white"
                style="background-color: rgba(0, 0, 0, 0.5)"
              >
                {{ $t('showing.predictedAttendanceLevel') }}
              </q-tooltip>
            </q-badge>
          </div>
        </div>
        <!-- Remaining seats details (sold, available, etc) -->
        <div class="row justify-between q-my-lg">
          <div
            class="col column items-center text-primary q-mx-sm"
            style="flex-basis: auto;"
            :style="{ 'flex-grow': (showing.ticketsSold / showing.screen.seatCount * 100) }"
          >
            <div class="text-h4 text-center">
              {{ showing.ticketsSold }}
            </div>
            <div class="text-bold text-center">
              {{ $t('status.sold') }}
            </div>
          </div>
          <div
            class="col column items-center q-mx-sm"
            :class="'text-' + availableColor"
            style="flex-basis: auto;"
            :style="{ 'flex-grow': (seatsAvailable / showing.screen.seatCount * 100) }"
          >
            <div
              class="text-h4 text-center"
              data-test-id="held-seat-dialog-available-count"
            >
              {{ seatsAvailable }}
            </div>
            <div class="text-bold text-center">
              {{ $t('status.available') }}
            </div>
          </div>
          <div
            v-if="showing.reservedSeating && socialDistanceCount"
            class="col column items-center q-mx-sm text-grey-6"
            style="flex-basis: auto;"
            :style="{ 'flex-grow': (socialDistanceCount / showing.screen.seatCount * 100) }"
          >
            <div class="text-h4 text-center">
              {{ socialDistanceCount }}
            </div>
            <div class="text-bold text-center">
              {{ $t('common.socialDistance') }}
            </div>
          </div>
          <div
            v-if="showing.hasSeatChart && unavailableCount"
            class="col column items-center q-mx-sm text-grey-7"
            style="flex-basis: auto;"
            :style="{ 'flex-grow': (unavailableCount / showing.screen.seatCount * 100) }"
          >
            <div
              class="text-h4 text-center"
              data-test-id="held-seat-dialog-unavailable-count"
            >
              {{ unavailableCount }}
            </div>
            <div class="text-bold text-center">
              {{ $t('status.unavailable') }}
            </div>
          </div>
          <div
            v-if="!showing.hasSeatChart"
            class="col column items-center text-grey-7 q-mx-sm cursor-pointer"
            style="flex-basis: auto;"
            :style="{ 'flex-grow': (heldSeats / showing.screen.seatCount * 100) }"
            @click="editHeldCount = !editHeldCount"
          >
            <div
              class="text-h4 text-center"
              style="position: relative;"
            >
              {{ heldSeats }}
              <q-icon
                class="absolute"
                style="right: -25px; top: 0"
                name="edit"
                size="xs"
              />
            </div>
            <div class="text-bold text-center">
              {{ $t('status.held') }}
            </div>
          </div>
          <div
            class="col column items-center q-ml-md"
            style="flex-basis: auto;"
            :style="{ 'flex-grow': (showing.screen.seatCount / 15 / showing.screen.seatCount * 100) }"
          >
            <div class="text-h4 text-center">
              {{ showing.screen.seatCount }}
            </div>
            <div class="text-bold text-center">
              {{ $t('collection.total') }}
            </div>
          </div>
        </div>
        <!-- Seat chart -->
        <div
          v-if="showing.hasSeatChart"
          class="row justify-center"
          style="max-width: 500px;"
        >
          <seat-chart
            ref="seatChart"
            style="max-width: 500px;"
            class="col"
            :seat-chart="seatChartForShowing"
            :seat-chart-options="seatChartForShowing.seatChartOptions"
            :show-seat-numbers="currentSite.consumerShowReservedSeatLabels"
            :hide-unavailable-seat-numbers="true"
            :seats-to-select="0"
            :has-marathon="showing.hasMarathon"
            :show-footer="false"
            :show-header="false"
            :available-mode="true"
            @edit-held-seats="editHeldSeatSections = true"
            @update:model-value="shown = false; $emit('select-showing', showing)"
          />
        </div>

        <div
          v-if="!showing.hasSeatChart && editHeldCount"
          class="row justify-center"
        >
          <q-input
            v-model.number="heldSeats"
            type="number"
            min="0"
            :max="showing.screen.seatCount"
            input-style="font-size: 18px; text-align: center;"
            class="col-auto"
            style="min-width: 150px;"
            :label="$t('seatChart.newHeldSeatCount')"
            :rules="[val => val !== '' || $t('form.errors.required'), val => val >= 0 || $t('form.errors.cannotBeLessThanAmount', { amount: 0 }), val => val <= (showing.screen.seatCount - showing.ticketsSold) || $t('seatChart.errors.mustNotExceedSeatCountTicketsSoldCount', { count: showing.screen.seatCount - showing.ticketsSold })]"
            bottom-slots
          />
        </div>
      </q-card-section>
      <q-card-actions
        class="col-auto row justify-center q-ma-sm"
      >
        <q-btn
          v-if="!showing.hasSeatChart && editHeldCount"
          :label="$t('seatChart.actions.setHeldSeatCount')"
          class="col"
          size="lg"
          :color="canSave ? 'primary' : 'grey'"
          :disable="!canSave"
          :loading="showing.loading"
          @click="setHeldSeatCount"
        />
        <q-btn
          v-else
          :label="$tc('order.viewOrders', 2)"
          data-test-id="view-orders-button"
          @click="$refs.ordersByShowDialog.show(showing.id, null, null, null)"
        />
      </q-card-actions>
      <order-filter-by-model-dialog
        ref="ordersByShowDialog"
        @select-order="order => { $emit('select-order', order); shown = false }"
        @request-authorization="callback => $emit('request-authorization', callback)"
      />
      <released-sections-dialog
        v-if="showing.hasSeatChart"
        v-model="editHeldSeatSections"
        :showing="showing"
        :seat-chart="seatChartForShowing"
        @request-authorization="callback => $emit('request-authorization', callback)"
      />
      <q-inner-loading :showing="showing.loading || seatChartForShowing.loading" />
    </q-card>
  </q-dialog>
</template>

<script>
import Showing from 'src/models/showing/Showing'
import ReleasedSectionsDialog from 'src/modules/admin/seat-chart/ReleasedSectionsDialog.vue'
import SeatChart from 'src/modules/admin/seat-chart/SeatChart.vue'
import ShowingCard from 'src/modules/register/components/ShowingCard'
import OrderFilterByModelDialog from 'src/modules/register/dialogs/OrderFilterByModelDialog.vue'

export default {
  name: 'HeldSeatDialog',
  components: {
    SeatChart,
    OrderFilterByModelDialog,
    ReleasedSectionsDialog,
    ShowingCard
  },
  emits: ['select-showing', 'select-order', 'request-authorization'],
  data () {
    return {
      tab: null,
      showing: null,
      shown: false,
      heldSeats: 0,
      editHeldCount: false,
      editHeldSeatSections: false
    }
  },
  models: {
    seatChartForShowing: {
      heldSeatSectionNames: true,
      seatChartOptions: true,
      seatChart: true,
      variables () {
        return {
          showingId: this.showing?.id || 0,
          skip: !this.showing || !this.showing.hasSeatChart
        }
      },
      force: true
    }
  },
  computed: {
    canSave () {
      return this.heldSeats !== '' && this.heldSeats >= 0 && this.heldSeats <= (this.showing.screen.seatCount - this.showing.ticketsSold) && this.heldSeats !== this.showing.heldSeatCount
    },
    seatsAvailable () {
      if (this.showing.hasSeatChart) {
        return this.showing.seatsRemaining
      } else {
        return this.showing.seatsRemaining + this.showing.heldSeatCount - (this.showing.reservedSeating ? 0 : this.heldSeats) - (!this.showing.hasMarathon ? 0 : Math.max(this.showing.marathonSeatCount - this.showing.marathonTicketsSold, 0))
      }
    },
    socialDistanceCount () {
      return this.showing.seatsRemainingWithoutSocialDistancing - this.showing.seatsRemaining
    },
    unavailableCount () {
      return this.showing.screen.seatCount - this.showing.seatsRemainingWithoutSocialDistancing - this.showing.ticketsSold
    },
    availableColor () {
      const ratio = this.seatsRemaining / this.showing.screen.seatCount - this.showing.heldSeatCount

      if (ratio < 0.25) {
        return 'negative'
      } else if (ratio < 0.5) {
        return 'warning'
      } else {
        return 'positive'
      }
    }
  },
  configuration: ['currentSite'],
  watch: {
    showing () {
      if (this.showing) this.heldSeats = this.showing.heldSeatCount
    },
    tab () {
      if (this.showing.id !== this.tab) {
        const marathonShowing = this.showing.showings.data.find(marathonShowing => marathonShowing.id === this.tab)
        this.$nextTick(() => {
          this.editHeldCount = false
          this.loadShowing(marathonShowing.data)
        })
      }
    },
    shown () {
      if (!this.shown) {
        this.showing?.destroy()
        this.showing = null
      }
    }
  },
  beforeUnmount () {
    this.showing?.destroy()
  },
  methods: {
    show (showing) {
      this.shown = true
      this.editHeldCount = false

      this.tab = showing.id

      this.loadShowing(showing.data)
    },
    loadShowing (showingData) {
      if (this.showing) {
        this.showing.destroy()
      }

      this.showing = new Showing(
        showingData,
        {
          fields: ['id', 'seatsRemaining', 'seatsRemainingWithoutSocialDistancing', 'ticketsSold', 'customHeldSeatCount', 'overrideHeldSeatCount', 'releasedSections']
        }
      )
      this.showing.fetch(true)
    },
    async setHeldSeatCount () {
      this.showing.overrideHeldSeatCount = true
      this.showing.customHeldSeatCount = this.heldSeats
      await this.showing.save()
      this.editHeldCount = false
    }
  }
}
</script>

<style lang="scss" scoped>
.showing-card-section {
  max-width: 500px;
  min-width: 500px;
  width: 500px;
}

.seat-progress-bar {
  border: solid 1px #CCC;
  height: 20px;
  border-radius: 10px;
  overflow: hidden;
}

.seat-progress-bar > div {
  display: inline-block;
  height: 20px;
}
</style>
